import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";

import App from "@/App";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { AppShell } from "@/components/layout/AppShell";
import { AdminRoute } from "@/routes/AdminRoute";
import { AdminPublicRoute } from "@/routes/AdminPublicRoute";
import { ProtectedRoute } from "@/routes/ProtectedRoute";
import { PublicRoute } from "@/routes/PublicRoute";

const LoginPage = lazy(() => import("@/pages/auth/LoginPage"));
const AdminLoginPage = lazy(() => import("@/pages/auth/AdminLoginPage"));
const RegisterPage = lazy(() => import("@/pages/auth/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/auth/ForgotPasswordPage"));
const DashboardPage = lazy(() => import("@/pages/app/DashboardPage"));
const DevicesPage = lazy(() => import("@/pages/app/DevicesPage"));
const FilesPage = lazy(() => import("@/pages/app/FilesPage"));
const FilePreviewPage = lazy(() => import("@/pages/app/FilePreviewPage"));
const PhotosPage = lazy(() => import("@/pages/app/PhotosPage"));
const PhotoEditorPage = lazy(() => import("@/pages/app/PhotoEditorPage"));
const SharedPage = lazy(() => import("@/pages/app/SharedPage"));
const SharedLinkPage = lazy(() => import("@/pages/app/SharedLinkPage"));
const TrashPage = lazy(() => import("@/pages/app/TrashPage"));
const SearchResultsPage = lazy(() => import("@/pages/app/SearchResultsPage"));
const OfficePage = lazy(() => import("@/pages/app/OfficePage"));
const OCRViewerPage = lazy(() => import("@/pages/app/OCRViewerPage"));
const AppSettingsPage = lazy(() => import("@/pages/app/SettingsPage"));
const AdminDashboardPage = lazy(() => import("@/pages/admin/AdminDashboardPage"));
const TenantsPage = lazy(() => import("@/pages/admin/TenantsPage"));
const TenantDetailPage = lazy(() => import("@/pages/admin/TenantDetailPage"));
const UsersPage = lazy(() => import("@/pages/admin/UsersPage"));
const StoragePage = lazy(() => import("@/pages/admin/StoragePage"));
const AuditPage = lazy(() => import("@/pages/admin/AuditPage"));
const SystemPage = lazy(() => import("@/pages/admin/SystemPage"));
const AdminSettingsPage = lazy(() => import("@/pages/admin/SettingsPage"));

function withSuspense(element: ReactNode) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingSpinner />}>
        {element}
      </Suspense>
    </ErrorBoundary>
  );
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    children: [
      { index: true, element: <Navigate replace to="/login" /> },
      { path: "/login", element: withSuspense(<PublicRoute><LoginPage /></PublicRoute>) },
      { path: "/admin/login", element: withSuspense(<AdminPublicRoute><AdminLoginPage /></AdminPublicRoute>) },
      { path: "/register", element: withSuspense(<PublicRoute><RegisterPage /></PublicRoute>) },
      { path: "/forgot-password", element: withSuspense(<PublicRoute><ForgotPasswordPage /></PublicRoute>) },
      { path: "/s/:token", element: withSuspense(<SharedLinkPage />) },
      {
        path: "/app",
        element: withSuspense(<ProtectedRoute><AppShell variant="user" /></ProtectedRoute>),
        children: [
          { index: true, element: <Navigate replace to="/app/files" /> },
          { path: "home", element: withSuspense(<DashboardPage />) },
          { path: "devices", element: withSuspense(<DevicesPage />) },
          { path: "files", element: withSuspense(<FilesPage />) },
          { path: "files/:folderId", element: withSuspense(<FilesPage />) },
          { path: "files/preview/:fileId", element: withSuspense(<FilePreviewPage />) },
          { path: "photos", element: withSuspense(<PhotosPage />) },
          { path: "photos/edit/:fileId", element: withSuspense(<PhotoEditorPage />) },
          { path: "shared", element: withSuspense(<SharedPage />) },
          { path: "trash", element: withSuspense(<TrashPage />) },
          { path: "search", element: withSuspense(<SearchResultsPage />) },
          { path: "office/:fileId", element: withSuspense(<OfficePage />) },
          { path: "ocr/:fileId", element: withSuspense(<OCRViewerPage />) },
          { path: "settings", element: withSuspense(<AppSettingsPage />) },
        ],
      },
      {
        path: "/admin",
        element: withSuspense(<AdminRoute><AppShell variant="admin" /></AdminRoute>),
        children: [
          { index: true, element: withSuspense(<AdminDashboardPage />) },
          { path: "tenants", element: withSuspense(<TenantsPage />) },
          { path: "tenants/:id", element: withSuspense(<TenantDetailPage />) },
          { path: "users", element: withSuspense(<UsersPage />) },
          { path: "storage", element: withSuspense(<StoragePage />) },
          { path: "audit", element: withSuspense(<AuditPage />) },
          { path: "system", element: withSuspense(<SystemPage />) },
          { path: "settings", element: withSuspense(<AdminSettingsPage />) },
        ],
      },
    ],
  },
]);
